<template>
  <BaseCard
    title="Industry Sector Summary"
    class="h-full"
  >
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit">
        <div class="px-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.totalBusinesses.formatted"
                :prefix="summary.totalBusinesses.prefix"
                :suffix="summary.totalBusinesses.suffix"
                :decimal-places="summary.totalBusinesses.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Total Businesses
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.totalPrice.formatted"
                :prefix="summary.totalPrice.prefix"
                :suffix="summary.totalPrice.suffix"
                :decimal-places="summary.totalPrice.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Total Price (All Sectors)
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.averagePrice.formatted"
                :prefix="summary.averagePrice.prefix"
                :suffix="summary.averagePrice.suffix"
                :decimal-places="summary.averagePrice.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Average Price per Business (All Sectors)
              </p>
            </div>

            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-white border border-gray-200 p-6 rounded-md shadow-sm"
            >
              <BaseNumberRoll
                class="text-2xl font-bold text-primary"
                :end-val="summary.averageMultiple.formatted"
                :prefix="summary.averageMultiple.prefix"
                :suffix="summary.averageMultiple.suffix"
                :decimal-places="summary.averageMultiple.decimalPlaces"
              />
              <p class="text-sm text-gray-600 mt-1">
                Average Multiple (All Sectors)
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useDashboard } from '~/composables/useDashboardCA'
import { useUtils } from '~/composables/useUtils'

interface AnalyticsSummary {
  totalBusinesses: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  totalPrice: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  averagePrice: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
  averageMultiple: {
    prefix: string
    original: number
    formatted: string
    suffix: string
    decimalPlaces: number
  }
}

interface AnalyticsResponse {
  analytics: unknown[]
  summary: AnalyticsSummary
}

const { getIndustryAnalytics } = useDashboard()

const isLoading = ref(true)
const summary = ref<AnalyticsSummary>({
  totalBusinesses: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  totalPrice: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  averagePrice: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
  averageMultiple: {
    prefix: '',
    original: 0,
    formatted: '0',
    suffix: '',
    decimalPlaces: 0,
  },
})

const { numberRollFormatToCurrency, numberRollConvertToUnits } = useUtils()

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getIndustryAnalytics()
    const data = response?.data as AnalyticsResponse

    if (data) {
      const averagePricePerBusiness = data.summary.totalBusinesses > 0
        ? data.summary.totalPrice / data.summary.totalBusinesses
        : 0

      summary.value = {
        totalBusinesses: numberRollConvertToUnits(data.summary.totalBusinesses, ''),
        totalPrice: numberRollFormatToCurrency(data.summary.totalPrice),
        averagePrice: numberRollFormatToCurrency(averagePricePerBusiness),
        averageMultiple: numberRollConvertToUnits(data.summary.averageMultiple, 'x'),
      }
    }
  }
  catch (error) {
    console.error('Error fetching industry analytics summary:', error)
    summary.value = {
      totalBusinesses: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      totalPrice: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      averagePrice: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
      averageMultiple: {
        prefix: '',
        original: 0,
        formatted: '0',
        suffix: '',
        decimalPlaces: 0,
      },
    }
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
